import React, { useState, useRef, useEffect } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftLetter: string;
    rightImage: string;
}

const MatchObjectWithLetters: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');

    const modal = useRef<HTMLIonModalElement>(null);

    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };
        const elementRect = element.getBoundingClientRect();
        setHeight(container.scrollHeight + 'px');
        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget);
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);
            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    const letterImageMapping: Record<string, string> = {
        "A": "apple.png",
        "B": "ball.png",
        "C": "cat.png",
        "D": "dog.png",
        "E": "elephant.png",
        "F": "fish.png",
        "G": "grapes.png",
        "H": "hat.png",
        "I": "icecream.png",
        "J": "jug.png",
        "K": "kite.png",
        "L": "lion.png",
        "M": "monkey.png",
        "N": "nest.png",
        "O": "orange.png",
        "P": "parrot.png",
        "Q": "queen.png",
        "R": "rabbit.png",
        "S": "sun.png",
        "T": "tiger.png",
        "U": "umbrella.png",
        "V": "van.png",
        "W": "watch.png",
        "X": "xylophone.png",
        "Y": "yak.png",
        "Z": "zebra.png"
    }


    const fetchQuestions = async () => {
        const letters = Object.keys(letterImageMapping);
        const shuffledLetters = [...letters].sort(() => Math.random() - 0.5).slice(0, 4);
        const shuffledImages = [...shuffledLetters].sort(() => Math.random() - 0.5);
        const pairs = shuffledLetters.map((letter, index) => ({
            left_id: index + 1,
            leftLetter: letter,
            rightImage: letterImageMapping[shuffledImages[index]],
            right_id: shuffledLetters.indexOf(shuffledImages[index]) + 1
        }));
        console.log(pairs)
        setQuestions(pairs);
    };

    useEffect(() => {
        resetConnections();
        fetchQuestions();
        document.title = "Match Object with Letter – Nursery Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const nextQuestion = () => {
        resetConnections();
        setShowImage(true);
        setTimeout(() => {
            fetchQuestions();
            setShowImage(false);
        }, 3000);
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='The "Match Object with Letter" activity on Mamas Guidance is an engaging educational tool designed for nursery-level learners to enhance their letter recognition and vocabulary skills. This interactive activity encourages children to associate letters with corresponding objects, reinforcing their understanding of the alphabet and common word associations.' />
            </Helmet>
            {!showImage ? (
                <>
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Match Object with Letter</h1>

                            <p>
                                Make learning the alphabet fun with our <strong>Match Object with Letter</strong> worksheet! This engaging activity helps children build early literacy skills by <strong>matching objects to the letters they start with</strong>. It’s perfect for preschoolers who are just beginning to connect letters with sounds.
                            </p>

                            <h2>🔍 What’s in the Worksheet?</h2>
                            <p>
                                Children will see a mix of letters (<b>A–Z</b>) and colorful pictures of familiar objects like <em><b>apple, ball, cat</b></em>, and more. Their task is to <strong>draw a line from each object to the correct starting letter</strong> — helping them develop phonemic awareness and letter recognition.
                            </p>

                            <h2>🎯 What Your Child Will Learn</h2>
                            <ul>
                                <li>Identify the <strong>beginning sounds</strong> of words</li>
                                <li>Match letters (<strong>uppercase</strong> or <strong>lowercase</strong>) with related objects</li>
                                <li>Improve <strong>visual association</strong> and <strong>phonics skills</strong></li>
                                <li>Strengthen early <strong>vocabulary</strong> and reading readiness</li>
                            </ul>

                            <h2>👩‍🏫 How to Use This Worksheet</h2>
                            <ul>
                                <li>Start by pronouncing the name of each object with your child.</li>
                                <li>Ask: <strong>“What sound does this word begin with?”</strong></li>
                                <li>Find the matching letter and draw a line to it.</li>
                                <li>Say the letter and object together (e.g., <strong>“A is for Apple”</strong>).</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/english-language/match-letter"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Match Letters</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/english-language/object-from-letter"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Find Object from Letter</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/english-language/letter-from-object"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Find Letter from Object</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Matters</h2>
                            <p>
                                Associating objects with their <strong>beginning sounds</strong> is one of the first steps in <strong>phonics</strong>. It builds a <strong>strong foundation</strong> for reading, spelling, and speaking. Plus, children love learning with pictures!
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Match Object with Number – Nursery Maths Worksheet</h1>

                        <p>
                            Looking for a fun way to teach numbers to your toddler? This <strong>Match Object with Number</strong> worksheet helps nursery and preschool kids develop early math skills by <strong>connecting pictures to numbers</strong>. With colorful visuals and easy-to-follow instructions, learning numbers becomes playful!
                        </p>

                        <h2>🔍 What Does This Worksheet Do?</h2>
                        <p>
                            In this matching activity, children see groups of objects (like balloons, animals, or stars) on one side and numbers on the other. Their task is to <strong>draw lines to match the correct number of objects with the corresponding number</strong>.
                        </p>

                        <h2>🎯 What Will Your Child Learn?</h2>
                        <ul>
                            <li>Count objects accurately</li>
                            <li>Recognize numbers from 1 to 20</li>
                            <li>Match quantities with numerals</li>
                            <li>Improve hand-eye coordination and focus</li>
                        </ul>


                        <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                        <ul>
                            <li>First, count the objects aloud with your child.</li>
                            <li>Use a pencil or crayon to draw lines connecting the pictures to the right numbers.</li>
                            <li>Repeat the activity regularly to build confidence and speed.</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/nursery/mathematics/count-object"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Count Object</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/mathematics/missing-number"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Missing Number</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/mathematics/bigger-or-smaller"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Bigger Or Smaller</IonLabel>
                            </IonItem>
                        </div>



                        <h2>🧠 Why Matching Activities Work</h2>
                        <p>
                            <strong>Matching games</strong> connect visual learning with number sense. Children begin to understand that numbers represent quantities — a critical early math <strong>milestone</strong>. This worksheet builds that understanding in a <strong>joyful way</strong>.
                        </p>

                    </article>
                    <div ref={containerRef} className="contentPart additionWord matchObject match-container" style={{ position: "relative", height: "calc(-200px + 100vh)", marginTop: "0%" }}>
                        <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                            {connections.map((conn, index) => (
                                <line key={index} x1={conn.x1} y1={conn.y1} x2={conn.x2} y2={conn.y2} stroke={conn.correct ? "green" : "red"} strokeWidth="3" />
                            ))}
                        </svg>
                        {/* <h6>Match the Letter to its Corresponding Object</h6> */}
                        <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                            <div className="left-column">
                                {questions.map((item) => (
                                    <div key={item.left_id} className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`} onClick={(e) => handleLeftClick(e, item.left_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                        {item.leftLetter}
                                    </div>
                                ))}
                            </div>
                            <div className="right-column">
                                {questions.map((item) => (
                                    <div key={item.right_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                        <img src={process.env.REACT_APP_API_URL + item.rightImage} width={50} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-2 mb-4 text-center" style={{ display: 'flex', gap: '10px' }}>
                            <div className="optionButton background-blue" style={{ fontSize: '18px' }} onClick={resetConnections}>Reset</div>
                            <div className="btn hero-btn" style={{ fontSize: '18px' }} onClick={nextQuestion}>Next &gt;&gt;</div>
                        </div>
                    </div>
                </>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                </div>
            )}
        </>
    );
};

export default MatchObjectWithLetters;
